import { createColumnHelper } from '@tanstack/react-table';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { commonMessages } from 'src/utils/messages';
import { Unit } from 'src/features/units/utils/types';
import { useListUnits } from 'src/features/units/api/useListUnits';
import { useIntl } from 'react-intl';
import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { unitsMessages } from 'src/features/units/utils/messages';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import CreateUnitFormModal from 'src/features/units/components/forms/CreateUnitFormModal';
import CellUnitActions from 'src/features/units/components/tables/CellUnitActions';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const UnitsTable = () => {
    const { formatMessage } = useIntl();

    const permissionManageUnits = useHasPermissions([PERMISSION.MANAGE_UNITS]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(unitsMessages.units));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { querySearchOptions, ...search } = useSearch();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'name', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: units,
        isError,
        isLoading,
    } = useListUnits({
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<Unit>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('abbreviation', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(unitsMessages.abbreviation),
            },
        }),
        columnHelper.accessor('code', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(unitsMessages.code),
            },
        }),
        ...(permissionManageUnits
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellUnitActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<Unit, unknown>
            columns={columns}
            data={units}
            semantics={{
                title: formatMessage(unitsMessages.units),
                entryName: formatMessage(unitsMessages.unit).toLowerCase(),
                entryNames: formatMessage(unitsMessages.units).toLowerCase(),
                placeholder: formatMessage(unitsMessages.unit).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageUnits && { newEntry: CreateUnitFormModal }),
            }}
        />
    );
};
export default UnitsTable;
